import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@sinohealth/butterfly-ui-components/lib';
import { request, requestFd } from '@/common/request';
import { getToken, removeToken } from '@/utils/cookies';
import {
  endIdleSession,
  getIdleSession,
  idleSessionKey,
  IDLE_SESSION_PREFIX,
  IDLE_TIMEOUT_MESSAGE,
  IDLE_TIMEOUT_MS,
  isIdleSessionActive,
  recordIdleActivity,
} from './idleSession';
import { SECURITY_EVENT } from './storage';

const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'click',
  'keydown',
  'input',
  'wheel',
  'touchstart',
  'touchmove',
];

export default function useIdleSession(token: string, enabled: boolean) {
  const navigate = useNavigate();
  const active = !enabled || !token || isIdleSessionActive(token);

  useEffect(() => {
    if (!enabled || !token) return undefined;
    let stopped = false;
    let timer = 0;
    let lastActivity = 0;
    let deadline = getIdleSession(token).lastActiveAt + IDLE_TIMEOUT_MS;

    const finish = (timeout: boolean) => {
      if (stopped) return;
      stopped = true;
      window.clearTimeout(timer);
      if (timeout) endIdleSession(token, 'timeout');
      removeToken();
      Modal.destroyAll();
      navigate(timeout ? '/login?reason=idle-timeout' : '/login', { replace: true });
    };
    const check = () => {
      if (stopped) return false;
      const currentToken = getToken();
      if (currentToken && currentToken !== token) {
        // Another tab signed into a new session; never clear its token from a stale timer.
        stopped = true;
        window.clearTimeout(timer);
        window.dispatchEvent(new Event(SECURITY_EVENT));
        return false;
      }
      const state = getIdleSession(token);
      if (!isIdleSessionActive(token)) {
        finish(state.ended !== 'logout');
        return false;
      }
      if (!currentToken) {
        finish(false);
        return false;
      }
      deadline = state.lastActiveAt + IDLE_TIMEOUT_MS;
      return true;
    };
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (check()) schedule();
      }, Math.max(0, deadline - Date.now()));
    };
    const onActivity = (event: Event) => {
      if (!event.isTrusted) return;
      const now = Date.now();
      // Limit high-frequency pointer/scroll writes, but never skip an expired deadline.
      if (now - lastActivity < 1000 && now < deadline && !stopped) return;
      if (!check()) {
        if (event.cancelable) event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (document.visibilityState === 'hidden') return;
      if (recordIdleActivity(token)) {
        lastActivity = now;
        deadline = getIdleSession(token).lastActiveAt + IDLE_TIMEOUT_MS;
        schedule();
      }
    };
    const onResume = () => {
      // Restoring a tab is a check, not activity; it must not reset the deadline.
      if (check()) schedule();
    };
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === 'access_token' ||
        event.key?.startsWith(IDLE_SESSION_PREFIX)
      ) {
        if (event.key === null || (event.key === idleSessionKey(token) && !event.newValue)) {
          endIdleSession(token);
        }
        onResume();
      }
    };
    const guardRequest = (config: any) => {
      if (check()) return config;
      // Match common's rejection contract to suppress a duplicate generic server-error toast.
      return Promise.reject(
        Object.assign(new Error(IDLE_TIMEOUT_MESSAGE), {
          response: {
            config: { ...config, noAuthentication: true },
            data: { success: false, errCode: 'IDLE_SESSION_ENDED', errMessage: IDLE_TIMEOUT_MESSAGE },
          },
        }),
      );
    };
    if (!check()) return undefined;
    const requestGuard = request.interceptors.request.use(guardRequest);
    const formGuard = requestFd.interceptors.request.use(guardRequest);
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { capture: true, passive: false }),
    );
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onResume);
    schedule();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      request.interceptors.request.eject(requestGuard);
      requestFd.interceptors.request.eject(formGuard);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity, true));
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onResume);
    };
  }, [enabled, token, navigate]);

  return active;
}
